import type {
  ActionResult,
  AriaController,
  StartChatOptions,
} from '@wandb/forge-shell-contract/aria';
import {useMemo} from 'react';
import {useSyncExternalStore} from 'use-sync-external-store/shim';

import {useShellAriaController} from '../../../shell/aria/ShellAriaControllerContext';
import {
  publishExternalWBAgentTurnCreated,
  subscribeWBAgentCrossTabEvents,
} from '../crossTabSync';
import type {ContentPart} from '../store/thread';
import {
  markThreadReadAndClearUnseen,
  observeThreadAsRead,
} from '../threadReadStatus';
import {type WBAgentControllerStore, wbAgentControllerStore} from './store';

/**
 * useWBAgentController exposes the agent chat, whether it's available and
 * various interactions, to any component in the app. `store` defaults to the
 * shared app-wide instance; tests pass their own for isolation.
 */
export function useWBAgentController(
  store: WBAgentControllerStore = wbAgentControllerStore
): AriaController {
  const shellController = useShellAriaController();
  const localController = useLocalWBAgentController(store);
  return shellController === undefined ? localController : shellController;
}

/** The local runtime API, also used by Aria to register with the shell. */
export function useLocalWBAgentController(
  store: WBAgentControllerStore = wbAgentControllerStore
): AriaController {
  // NOTE: isAvailable trails the live runtime by one commit. Fine for buttons,
  // but never gate a useAui-rendering component on it; those belong inside
  // AgentChatRuntimeBoundary.
  const {isAvailable} = useSyncExternalStore(
    store.subscribe,
    store.getAvailability
  );
  const actions = useMemo(
    () => ({
      startChat: (
        prompt: string | ContentPart[],
        opts: StartChatOptions
      ): ActionResult<'readonly' | 'empty' | 'occupied' | 'unavailable'> =>
        store.getHandle()?.startChat(prompt, opts) ?? {
          ok: false,
          reason: 'unavailable',
        },
      openChat: () => store.getHandle()?.openChat(),
      openChatToThread(threadId: string) {
        store.getHandle()?.openChatToThread(threadId);
      },
      registerExternalTurn({
        threadId,
        turnId,
      }: {
        threadId: string;
        turnId: string;
      }) {
        try {
          publishExternalWBAgentTurnCreated({
            type: 'turn-created',
            threadId,
            turnId,
          });
        } catch {
          // The persisted turn remains authoritative when notification fails.
        }
      },
      subscribeExternalTurnCreated(listener: () => void) {
        return subscribeWBAgentCrossTabEvents(event => {
          if (event.type === 'turn-created') {
            listener();
          }
        });
      },
      observeThreadAsRead,
      markThreadRead: markThreadReadAndClearUnseen,
    }),
    [store]
  );

  return useMemo(() => ({isAvailable, ...actions}), [isAvailable, actions]);
}
